import { filterClasses } from "@swivl-mono/helpers";
import parsePhoneNumberFromString, { AsYouType, validatePhoneNumberLength } from "libphonenumber-js";
import { forwardRef, useEffect, useId, useMemo, useRef, useState } from "react";

import { ErrorText } from "../FormField/FormField.ErrorText";
import { HelperText } from "../FormField/FormField.HelperText";
import { getFormFieldWidthClass } from "../FormField/FormField.Helpers";
import { Label } from "../FormField/FormField.Label";
import { FormFieldWidth } from "../FormField/FormField.Types";
import { InputSize, InputVariant } from "./Input";
import { COUNTRY_CODES } from "./Input.Constants";
import { InputField, type InputFieldProps } from "./Input.Field";
import { InputSelectPrefix, type SelectPrefixOption } from "./Input.SelectPrefix";

export interface PhoneInputProps
  extends Omit<InputFieldProps, "error" | "hasPrefix" | "hasSuffix" | "inputSize" | "variant" | "onChange" | "onBlur"> {
  label?: string;
  helperText?: string;
  error?: string;
  inputSize?: InputSize;
  variant?: InputVariant;
  containerClassName?: string;
  width?: FormFieldWidth;
  required?: boolean;
  countryCodeOptions?: SelectPrefixOption[];
  defaultCountryCode?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  searchable?: boolean;
  /**
   * When true, surface a short message when the entered number is longer than
   * the selected country allows (e.g. "United States numbers are 10 digits.").
   * The over-long value is still shown — this only validates and messages, it
   * does not block typing. Off by default so existing consumers are unaffected.
   */
  validateLength?: boolean;
}

const NON_DIGIT_REGEX = /\D/g;

/**
 * Format the raw national digits for display as the user types, using
 * libphonenumber's locale-aware grouping for the selected calling code,
 * normalized to dash separators (e.g. "7282830023" → "728-283-0023").
 * Display-only — the value emitted via `onChange` stays raw digits.
 */
function formatNationalNumber(countryCode: string, digits: string): string {
  if (!digits) return "";
  const callingCode = countryCode.replace(NON_DIGIT_REGEX, "");
  try {
    const formatted = new AsYouType(callingCode ? { defaultCallingCode: callingCode } : undefined).input(digits);
    const dashed = formatted.replace(/[()]/g, "").trim().replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
    // Guard against a formatter quirk dropping digits — fall back to raw.
    return dashed.replace(NON_DIGIT_REGEX, "") === digits ? dashed : digits;
  } catch {
    return digits;
  }
}

function parseInitialPhone(value: string | undefined, fallbackCountryCode: string): { countryCode: string; localNumber: string } {
  if (!value) return { countryCode: fallbackCountryCode, localNumber: "" };
  const parsed = parsePhoneNumberFromString(value);
  if (parsed) {
    return { countryCode: `+${parsed.countryCallingCode}`, localNumber: parsed.nationalNumber };
  }
  return { countryCode: fallbackCountryCode, localNumber: value };
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      label,
      helperText,
      error,
      inputSize = InputSize.MEDIUM,
      variant = InputVariant.OUTLINED,
      containerClassName,
      width = FormFieldWidth.FULL,
      required,
      id,
      countryCodeOptions = COUNTRY_CODES,
      defaultCountryCode = "+1",
      defaultValue,
      onChange,
      onBlur,
      searchable = true,
      validateLength = false,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const containerWidthClass = getFormFieldWidthClass(width);
    const resolvedContainerClassName = filterClasses(containerWidthClass, containerClassName);
    const initial = useMemo(() => parseInitialPhone(defaultValue, defaultCountryCode), [defaultValue, defaultCountryCode]);
    const [countryCode, setCountryCode] = useState(initial.countryCode);
    const [localNumber, setLocalNumber] = useState(initial.localNumber);
    // Message shown when enforceValidLength blocks further digits. Cleared as
    // soon as the user edits the number again.
    const [lengthLimitMessage, setLengthLimitMessage] = useState<string | undefined>(undefined);
    const prevDefaultValueRef = useRef(defaultValue);

    useEffect(() => {
      if (prevDefaultValueRef.current === defaultValue) return;
      prevDefaultValueRef.current = defaultValue;
      setCountryCode(initial.countryCode);
      setLocalNumber(initial.localNumber);
      setLengthLimitMessage(undefined);
    }, [defaultValue, initial.countryCode, initial.localNumber]);
    const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // When the number is longer than the selected country allows, set a message
    // naming the expected length (e.g. "United States numbers are 10 digits.").
    // The over-long value is left intact — this messages, it doesn't truncate.
    // No-op unless validateLength is on.
    const updateLengthMessage = (code: string, digits: string): void => {
      if (!validateLength || validatePhoneNumberLength(code + digits) !== "TOO_LONG") {
        setLengthLimitMessage(undefined);
        return;
      }
      // The expected length is where the number stops being "too long" — found
      // by trimming a throwaway copy from the right.
      let validDigits = digits;
      while (validDigits.length > 0 && validatePhoneNumberLength(code + validDigits) === "TOO_LONG") {
        validDigits = validDigits.slice(0, -1);
      }
      const countryLabel = countryCodeOptions.find((option) => option.value === code)?.label;
      setLengthLimitMessage(
        countryLabel
          ? `${countryLabel} numbers are ${validDigits.length} digits.`
          : `Phone numbers can't be longer than ${validDigits.length} digits.`
      );
    };

    const handleCountryCodeChange = (newCode: string) => {
      setCountryCode(newCode);
      updateLengthMessage(newCode, localNumber);
      onChange?.(newCode + localNumber);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let digits = e.target.value.replace(NON_DIGIT_REGEX, "");
      // If the user pasted or typed a number that already includes the
      // selected country code as digits (e.g. typed "19175551234" while
      // the "+1" prefix is selected, or pasted "+19175551234"), strip
      // those leading digits so we don't end up emitting "+119175551234".
      // Caught 2026-06-09 — `+119174069134` showed up in the DB because
      // the user typed +1 then 10 digits, the + was stripped, and the
      // remaining 11-digit string got concatenated under +1 again.
      const countryDigits = countryCode.replace(NON_DIGIT_REGEX, "");
      if (countryDigits && digits.startsWith(countryDigits) && digits.length > countryDigits.length) {
        digits = digits.slice(countryDigits.length);
      }
      updateLengthMessage(countryCode, digits);
      setLocalNumber(digits);
      onChange?.(countryCode + digits);
    };

    const handleInputBlur = () => {
      blurTimeoutRef.current = setTimeout(() => {
        blurTimeoutRef.current = null;
        onBlur?.();
      }, 0);
    };

    const handlePrefixOpenChange = (open: boolean) => {
      if (open && blurTimeoutRef.current !== null) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };

    // The specific length message (e.g. "…are 10 digits.") is more helpful than
    // a generic externally-supplied error, so it wins when present.
    const displayError = lengthLimitMessage ?? error;

    const InputContent = (
      <InputSelectPrefix
        inputSize={inputSize}
        error={!!displayError}
        options={countryCodeOptions}
        selectedValue={countryCode}
        onChange={handleCountryCodeChange}
        onOpenChange={handlePrefixOpenChange}
        searchable={searchable}
        disabled={props.disabled}
      >
        <div className="relative flex-1">
          <InputField
            ref={ref}
            id={inputId}
            error={!!displayError}
            hasPrefix={true}
            hasSuffix={false}
            inputSize={inputSize}
            variant={variant}
            type="tel"
            onBlur={handleInputBlur}
            value={formatNationalNumber(countryCode, localNumber)}
            onChange={handleInputChange}
            {...props}
          />
        </div>
      </InputSelectPrefix>
    );

    return (
      <div className={resolvedContainerClassName}>
        {label && (
          <Label htmlFor={inputId} required={required} className="mb-1">
            {label}
          </Label>
        )}
        {InputContent}
        {displayError && <ErrorText>{displayError}</ErrorText>}
        {!displayError && helperText && <HelperText>{helperText}</HelperText>}
      </div>
    );
  }
);

PhoneInput.displayName = "PhoneInput";
